import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { 
  X, 
  Upload, 
  Droplets, 
  Zap, 
  Sparkles, 
  Scale, 
  Camera, 
  FileText,
  Calendar,
  Clock,
  Loader2
} from 'lucide-react';
import { Job, Entry, EntryType, MediaAttachment } from '../types';
import { api } from '../api/client';

interface NewEntryModalProps {
  job: Job;
  initialDate?: Date;
  editingEntry?: Entry | null;
  onClose: () => void;
  onSave: (entry: Entry) => void;
}

export const NewEntryModal: React.FC<NewEntryModalProps> = ({
  job,
  initialDate,
  editingEntry,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState(editingEntry?.title || '');
  const [content, setContent] = useState(editingEntry?.content || '');
  const [categoryType, setCategoryType] = useState<EntryType>(editingEntry?.category_type || 'log');
  const [entryDate, setEntryDate] = useState<string>(
    editingEntry?.entry_date
      ? editingEntry.entry_date.slice(0, 16)
      : initialDate
      ? new Date(initialDate.getTime() - initialDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      : new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  );
  const [customFields, setCustomFields] = useState<Record<string, any>>(editingEntry?.custom_fields || {});
  const [attachments, setAttachments] = useState<MediaAttachment[]>(editingEntry?.attachments || []);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const availableFields = job.settings.customFields || [
    { id: 'f_temp', name: 'Temperature', type: 'number', unit: '°F' },
    { id: 'f_humidity', name: 'Humidity', type: 'number', unit: '%' },
    { id: 'f_light', name: 'Light Schedule', type: 'text', unit: 'hrs' },
  ];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const media = await api.uploadMedia(job.id, file);
        setAttachments((prev) => [...prev, media]);
      }
    } catch (err) {
      console.error(err);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      alert('Please enter a title for the log.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        job_id: job.id,
        title: title.trim(),
        content: content.trim(),
        entry_date: new Date(entryDate).toISOString(),
        category_type: categoryType,
        custom_fields: customFields,
        attachments,
      };

      let savedEntry: Entry;
      if (editingEntry) {
        await api.updateEntry(editingEntry.id, payload);
        savedEntry = { ...editingEntry, ...payload, updated_at: Math.floor(Date.now() / 1000) };
      } else {
        savedEntry = await api.addEntry(payload);
        if (categoryType === 'yield' || categoryType === 'milestone') {
          confetti({
            particleCount: 80,
            spread: 70,
            origin: { y: 0.6 },
          });
        }
      }

      onSave(savedEntry);
      onClose();
    } catch (err: any) {
      console.error(err);
      alert('Failed to save entry: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ fontSize: '1.35rem', color: 'var(--text-main)' }}>
            {editingEntry ? 'Edit Entry' : 'New Log Entry'}
          </h2>
          <button className="btn btn-secondary btn-icon" onClick={onClose} style={{ padding: '0.4rem' }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Category Selector */}
          <div className="form-group">
            <label className="form-label">Category / Action Type</label>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {[
                { type: 'log', label: 'Log', icon: <FileText size={13} /> },
                { type: 'watering', label: 'Watering', icon: <Droplets size={13} /> },
                { type: 'feeding', label: 'Feeding', icon: <Sparkles size={13} /> },
                { type: 'light', label: 'Light Cycle', icon: <Zap size={13} /> },
                { type: 'yield', label: 'Yield / Harvest', icon: <Scale size={13} /> },
                { type: 'milestone', label: 'Milestone', icon: <Sparkles size={13} /> },
                { type: 'photo', label: 'Photo Log', icon: <Camera size={13} /> },
              ].map((cat) => (
                <button
                  type="button"
                  key={cat.type}
                  className={`btn btn-sm ${categoryType === cat.type ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ borderRadius: 'var(--radius-full)', padding: '0.35rem 0.75rem' }}
                  onClick={() => setCategoryType(cat.type as EntryType)}
                >
                  {cat.icon}
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="form-group">
            <label className="form-label">Title / Headline *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Switched light to 18/6 or Fed 2L nutrient mix"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Date & Time */}
          <div className="form-group">
            <label className="form-label">Date & Time</label>
            <input
              type="datetime-local"
              className="form-input"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              required
            />
          </div>

          {/* Custom Fields (Job-Specific Metrics) */}
          <div className="form-group">
            <label className="form-label">Metrics & Custom Fields</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
              {availableFields.map((field) => (
                <div key={field.id}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>
                    {field.name} {field.unit ? `(${field.unit})` : ''}
                  </span>
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    step="any"
                    className="form-input"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.88rem' }}
                    placeholder={field.unit || 'Value'}
                    value={customFields[field.id] || ''}
                    onChange={(e) =>
                      setCustomFields({ ...customFields, [field.id]: e.target.value })
                    }
                  />
                </div>
              ))}

              {categoryType === 'yield' && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--accent-emerald-light)' }}>
                    Harvest Weight ({job.settings.yieldUnit || 'grams'})
                  </span>
                  <input
                    type="number"
                    step="any"
                    className="form-input"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.88rem' }}
                    placeholder="e.g. 240"
                    value={customFields['f_yield'] || ''}
                    onChange={(e) =>
                      setCustomFields({ ...customFields, f_yield: e.target.value })
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* Notes Content */}
          <div className="form-group">
            <label className="form-label">Notes & Observations</label>
            <textarea
              className="form-textarea"
              placeholder="Add observations, plant health notes, leaf color, watering schedule details..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
          </div>

          {/* Media Attachments */}
          <div className="form-group">
            <label className="form-label">Media Attachments (Photos & Videos)</label>
            <label className="dropzone" style={{ display: 'block' }}>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                disabled={isUploading}
              />
              {isUploading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Uploading to Cloudflare R2...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' }}>
                  <Upload size={22} color="var(--accent-emerald)" />
                  <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>Click to upload photos or videos</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-sub)' }}>
                    Saved to high-performance Cloudflare R2 Storage
                  </span>
                </div>
              )}
            </label>

            {attachments.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                {attachments.map((m, idx) => (
                  <div
                    key={m.id || idx}
                    style={{
                      position: 'relative',
                      width: 60,
                      height: 60,
                      borderRadius: 'var(--radius-sm)',
                      overflow: 'hidden',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <img src={m.url} alt={m.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button
                      type="button"
                      onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving || isUploading}>
              {isSaving ? 'Saving...' : editingEntry ? 'Save Changes' : 'Record Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
